import { getSupabase } from './client';
import { toDataError } from './errors';
import type { WorkspaceScope } from './workspace';

const BUCKET = 'work-files';
const SIGNED_URL_TTL_SECONDS = 60 * 60; // 1 hour — plenty for viewing/downloading in one sitting

export interface UploadedFile {
  readonly path: string;
  readonly name: string;
  readonly type: string;
  readonly size: number;
}

function objectPath(scope: WorkspaceScope, module: string, fileName: string): string {
  const safeName = fileName.replace(/[^A-Za-z0-9._-]/g, '_').slice(-120);
  const uid = crypto.randomUUID();
  return `${scope.workspaceId}/${scope.appKey}/${module}/${uid}-${safeName}`;
}

/** Uploads one file to the private work-files bucket; RLS gates it exactly like records.* (private.can). */
export async function uploadFile(scope: WorkspaceScope, module: string, file: File): Promise<UploadedFile> {
  const path = objectPath(scope, module, file.name);
  const { error } = await getSupabase().storage.from(BUCKET).upload(path, file, {
    contentType: file.type || 'application/octet-stream',
    upsert: false,
  });
  if (error) throw toDataError(error);
  return { path, name: file.name, type: file.type || 'application/octet-stream', size: file.size };
}

/**
 * Server-side copy of an already-uploaded file to a fresh path, for callers that attach one
 * upload to several records (e.g. the AI task-assignment flow giving the same reference file to
 * several assignees' tasks). Each record must own an independent Storage object — sharing one
 * `path` across records means any one of them removing "its" attachment deletes it for all the
 * others too, since remove() deletes the underlying object, not a per-record reference.
 */
export async function copyFile(scope: WorkspaceScope, module: string, source: UploadedFile): Promise<UploadedFile> {
  const path = objectPath(scope, module, source.name);
  const { error } = await getSupabase().storage.from(BUCKET).copy(source.path, path);
  if (error) throw toDataError(error);
  return { path, name: source.name, type: source.type, size: source.size };
}

/**
 * Short-lived URL to view/download a stored file — generated on demand, never persisted (any
 * copy saved into a record is just a stale hint; always re-fetch before actually opening a file).
 * Pass `downloadName` to force a browser download (Content-Disposition) even cross-origin,
 * where the HTML `download` attribute alone is ignored.
 */
export async function signedFileUrl(path: string, downloadName?: string): Promise<string> {
  const { data, error } = await getSupabase()
    .storage.from(BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL_SECONDS, downloadName ? { download: downloadName } : undefined);
  if (error) throw toDataError(error);
  return data.signedUrl;
}

/** Best-effort: callers should not fail the whole delete flow if this fails (e.g. already gone). */
export async function removeFile(path: string): Promise<void> {
  const { error } = await getSupabase().storage.from(BUCKET).remove([path]);
  if (error) throw toDataError(error);
}
